import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ButtonMenu, Tag, Avatar } from "@ann/web-components";
import { UsersContainer, PermissionsContainer, UserCellContainer, UserAvatarContainer, UserTextContainer } from "./Users.styled";
import { UserTableSection } from "./components";
import { useDebounce } from "../../../../../../../hooks";
import { useMembersContext } from "../../context";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";

// Extend dayjs with plugins
dayjs.extend(isToday);
dayjs.extend(isYesterday);

const Users = () => {
  const { knowledgeId } = useParams();
  const { members, membersMeta, loading: contextLoading, fetchMembers, updateMemberInState } = useMembersContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState("updated_at");
  const [orderDirection, setOrderDirection] = useState("DESC");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(null);

  // Debounce search keyword
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  // Track if search is pending (user is typing but debounce hasn't fired yet)
  const isSearching = searchKeyword !== debouncedSearchKeyword;

  // Format last active: "Today, 10:30AM", "Yesterday, 10:30AM", "Dec 15, 11:30 PM"
  const formatLastActive = useCallback((dateString) => {
    if (!dateString) return "—";

    const date = dayjs(dateString);
    const timeFormat = date.format("h:mm A");

    if (date.isToday()) {
      return `Today, ${timeFormat}`;
    }

    if (date.isYesterday()) {
      return `Yesterday, ${timeFormat}`;
    }

    // For older dates: "Dec 15, 11:30 PM"
    return date.format("MMM D, h:mm A");
  }, []);

  // Fetch members from context
  useEffect(() => {
    if (!knowledgeId) {
      console.warn("knowledgeId is missing, cannot fetch members");
      return;
    }

    // Map orderBy to API field names
    const orderByMap = {
      name: "user_full_name",
      email: "user_email",
      lastActive: "updated_at",
      role: "member_role",
    };
    const apiOrderBy = orderByMap[orderBy] || "updated_at";

    // Build search object if keyword is provided
    const searchObj = debouncedSearchKeyword
      ? {
          field: "user_full_name", // Default search field
          keyword: debouncedSearchKeyword,
        }
      : undefined;

    // Build filters object
    const filtersObj = {};
    if (selectedRole) {
      // Map display role back to API format
      const roleMapReverse = {
        Admin: "ADMIN",
        Approver: "APPROVER",
        Contributor: "CONTRIBUTER",
      };
      filtersObj.member_role = roleMapReverse[selectedRole] || selectedRole;
    }

    fetchMembers({
      knowledge_base_uuid: knowledgeId,
      page: currentPage,
      page_size: itemsPerPage,
      order_by: [apiOrderBy],
      order_direction: [orderDirection],
      search: searchObj,
      filters: Object.keys(filtersObj).length > 0 ? filtersObj : undefined,
    }, { persistAsBase: false });
  }, [knowledgeId, currentPage, itemsPerPage, orderBy, orderDirection, debouncedSearchKeyword, selectedRole, selectedPermission, fetchMembers]);

  // Map members from context to format expected by component
  const users = useMemo(() => {
    if (!members || !Array.isArray(members)) return [];

    return members.map((member) => {
      const roleMap = {
        ADMIN: "Admin",
        APPROVER: "Approver",
        CONTRIBUTER: "Contributor",
      };
      const memberRole = member.memberRole || member.member_role;
      const displayRole = roleMap[memberRole] || memberRole;

      return {
        userUuid: member.memberUuid || member.member_uuid,
        id: member.memberUuid || member.member_uuid,
        fullName: member.userFullName || member.user_full_name,
        full_name: member.userFullName || member.user_full_name,
        email: member.userEmail || member.user_email,
        role: displayRole,
        member_role: memberRole,
        permissions: selectedPermission || "",
        lastActive: member.updatedAt || member.updated_at,
        last_active: member.updatedAt || member.updated_at,
        user_avatar_url: member.userImgUrl || member.user_img_url,
        userAvatarUrl: member.userImgUrl || member.user_img_url,
        status: member.status,
      };
    });
  }, [members, selectedPermission]);

  // Extract meta from context
  const meta = useMemo(() => {
    const metaData = membersMeta?.pagination || membersMeta || {};
    return {
      totalItems: metaData.totalItems || metaData.total_items || 0,
      totalPages: metaData.totalPages || metaData.total_pages || 0,
      currentPage: metaData.page || metaData.current_page || currentPage,
      perPage: metaData.pageSize || metaData.page_size || metaData.per_page || itemsPerPage,
    };
  }, [membersMeta, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((selectedOption) => {
    const validOrderBy = ["name", "email", "lastActive", "role"];
    const newOrderBy = validOrderBy.includes(selectedOption) ? selectedOption : "lastActive";
    setOrderBy(newOrderBy);
    setCurrentPage(1);
  }, []);

  const handleOrderChange = useCallback((currentOrder) => {
    const newOrder = currentOrder === 'asc' ? 'DESC' : 'ASC';
    setOrderDirection(newOrder);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((keyword) => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
  }, []);

  const handleRoleChange = useCallback((role) => {
    setSelectedRole(role);
    setCurrentPage(1);
  }, []);

  const handlePermissionsChange = useCallback((permission) => {
    setSelectedPermission(permission);
    setCurrentPage(1);
  }, []);

  const handleUserPermissionChange = useCallback((userId, newPermission) => {
    // Find the member by userUuid
    const member = members.find(
      (m) => (m.userUuid || m.user_uuid) === userId
    );
    
    if (member) {
      const memberUuid = member.memberUuid || member.member_uuid;
      // Update member in context state (optimistic update)
      // Note: This is a local state update. The actual API call should be implemented separately
      // TODO: Implement API call to update user permission
      // Example: await updateMember({ knowledge_base_uuid: knowledgeId, member_uuid: memberUuid, user_uuid: userId, new_role: ... });
      updateMemberInState(memberUuid, { permissions: newPermission });
      console.log(`Update user ${userId} permission to ${newPermission}`);
    }
  }, [members, updateMemberInState]);

  // Permission options for ButtonMenu in table cells
  const permissionOptions = [
    { label: "View", value: "View" },
    { label: "Edit", value: "Edit" },
    { label: "Suggest", value: "Suggest" },
  ];

  // Get role color for Tag
  const getRoleColor = useCallback((role) => {
    const normalizedRole = typeof role === 'string' ? role.trim() : '';
    
    switch (normalizedRole) {
      case 'Admin':
        return 'lightBlue';
      case 'Approver':
        return 'neutral';
      case 'Contributor':
        return 'success';
      default:
        return 'neutral';
    }
  }, []);

  // Generate avatar URL using ui-avatars.com service
  const getAvatarUrl = useCallback((userName) => {
    if (!userName || userName === "—") {
      return null;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=2173FF&color=fff`;
  }, []);

  // Map API response to table data
  const tableData = users.map((user, rowIndex) => {
    const userId = user.userUuid || user.id;
    const userPermission = user.permissions || "";
    const permissionsLabel = userPermission || "No permissions";

    const userRole = user.role || "";
    const roleDisplay = userRole || "—";
    const userName = user.fullName || user.full_name || "—";
    
    // Use avatar URL from API/mock, fallback to generated URL if not provided
    const avatarSource = user.user_avatar_url || user.userAvatarUrl;
    const avatarUrl = avatarSource || getAvatarUrl(userName);
    const isLastRow = rowIndex === users.length - 1;

    return {
      id: userId,
      name: userName === "—" ? "—" : (
        <UserCellContainer>
          <UserAvatarContainer>
            <Avatar
              size="md"
              src={avatarUrl}
              alt={userName}
            />
          </UserAvatarContainer>
          <UserTextContainer>
            {userName}
          </UserTextContainer>
        </UserCellContainer>
      ),
      email: user.email || "—",
      role: roleDisplay === "—" ? (
        "—"
      ) : (
        <Tag
          color={getRoleColor(userRole)}
          size="small"
          text={roleDisplay}
        />
      ),
      permissions: (
        <PermissionsContainer>
          <ButtonMenu
            label={permissionsLabel}
            menuPosition={isLastRow ? "top" : "bottom"}
            alignMenu="left"
            onChange={(value) => {
              handleUserPermissionChange(userId, value);
            }}
            size="medium"
            type="secondary"
            width="auto"
          >
            <ButtonMenu.Menu>
              {permissionOptions.map((option) => {
                return (
                  <ButtonMenu.Item
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  >
                    {option.label}
                  </ButtonMenu.Item>
                );
              })}
            </ButtonMenu.Menu>
          </ButtonMenu>
        </PermissionsContainer>
      ),
      lastActive: formatLastActive(user.lastActive || user.last_active),
    };
  });

  const headers = [
    { label: 'Name', value: 'name' },
    { label: 'Email', value: 'email' },
    { label: 'Role', value: 'role' },
    { label: 'Permissions', value: 'permissions' },
    { label: 'Last Active', value: 'lastActive' }
  ];

  const getSelectedSortOption = () => {
    const validOrderBy = ["name", "email", "lastActive", "role"];
    return validOrderBy.includes(orderBy) ? orderBy : "lastActive";
  };

  const getOrderDirection = () => {
    return orderDirection.toLowerCase();
  };

  return (
    <UsersContainer>
      <UserTableSection
        tableData={tableData}
        headers={headers}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onPageChange={handlePageChange}
        totalItems={meta.totalItems || 0}
        totalPages={meta.totalPages || 0}
        loading={contextLoading || isSearching}
        onSortChange={handleSortChange}
        onOrderChange={handleOrderChange}
        selectedSortOption={getSelectedSortOption()}
        orderDirection={getOrderDirection()}
        onSearchChange={handleSearchChange}
        onRoleChange={handleRoleChange}
        onPermissionsChange={handlePermissionsChange}
      />
    </UsersContainer>
  );
};

export default Users;
